import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import Loading from '@whitelabel/rentalcover-shared/components/Loading';
import { useGetRCCustomerQuery } from '@whitelabel/cp-shared/store/claimsPortalAPI/customer';
import { bwIdentify } from '@whitelabel/cp-shared/helpers/analytics';
import { initFeatureFlags } from '@whitelabel/xcover-shared/helpers/featureFlag';
import { initCPDDFeatureFlags } from '@whitelabel/cp-shared/helpers/featureFlags';
import claimsPortalMessages from '@whitelabel/cp-shared/helpers/messages/commonMsg';
import {
  captureAgentToken,
  clearAgentSessionForOtherBooking,
  clearClaimsActor,
  getAgentToken,
  getRCAdminBookingURL,
  isAgentSessionExpired,
} from '@whitelabel/cp-shared/helpers/agentSession';
import { useAgentSession } from '@whitelabel/cp-shared/helpers/hooks/useAgentSession';
import { IProtectedLayoutParams } from '@whitelabel/cp-shared/helpers/types';
import { useAppDispatch } from '../../../store';
import { setCustomer } from '../../../store/slices/customer';
import ErrorPage from '../../../components/ErrorPage';
import { useGetRefreshTokenMutation, useGetTokenStatusQuery } from '../../../store/endpoints/auth';
import { useTypedRouter } from '../../../helpers/hooks';

export interface IProtectedLayoutProps {
  children: React.ReactNode;
}

const ProtectedLayout = ({ children }: IProtectedLayoutProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { location, locale, query, replace } = useTypedRouter<IProtectedLayoutParams & { agent_token?: string }>();
  const { bookingID, agent_token: agentTokenParam } = query;
  const { isExpired: isAgentExpired } = useAgentSession();
  const { currentData: tokenStatus } = useGetTokenStatusQuery(undefined, { refetchOnFocus: true });

  const {
    data: customer,
    isFetching: isFetchingRCCustomer,
    isError: isGetCustomerError,
  } = useGetRCCustomerQuery(undefined, { skip: tokenStatus !== 'valid' });

  const [getRCRefreshToken, { isLoading: isLoadingRefreshToken, isError: isGetRefreshTokenError }] =
    useGetRefreshTokenMutation();

  // Support agent launched from RC Admin — keep the agent token for this tab and strip it from the URL
  // while preserving bookingID and any other query params.
  useEffect(() => {
    const sourceQuery = location?.search ? Object.fromEntries(new URLSearchParams(location.search)) : { ...query };
    const queryWithoutAgentToken = captureAgentToken(sourceQuery);
    if (queryWithoutAgentToken) {
      const nextSearch = new URLSearchParams(
        Object.entries(queryWithoutAgentToken).filter(([, value]) => typeof value === 'string') as [string, string][],
      ).toString();
      replace({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' });
    } else {
      clearAgentSessionForOtherBooking(bookingID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentTokenParam, bookingID, location?.pathname, location?.search, replace]);

  useEffect(() => {
    if (getAgentToken() && isAgentSessionExpired()) clearClaimsActor();
  }, [isAgentExpired]);

  useEffect(() => {
    if (tokenStatus === 'none' || isGetRefreshTokenError) {
      window.location.replace(
        `/${locale}/auth/sign-in/?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`,
      );
    } else if (tokenStatus === 'expired') {
      void getRCRefreshToken();
    } else if (customer) {
      dispatch(setCustomer({ customer }));
      if (!getAgentToken()) bwIdentify(customer);
      initFeatureFlags(customer.id, locale);
      void initCPDDFeatureFlags('rc-claims-portal-frontend', {
        customerId: customer.id,
        email: customer.email,
        locale: locale ?? 'en',
      });
    }
  }, [
    customer,
    dispatch,
    getRCRefreshToken,
    isGetRefreshTokenError,
    locale,
    location.pathname,
    location.search,
    tokenStatus,
  ]);

  if (isAgentExpired) {
    const rcAdminBookingURL = getRCAdminBookingURL(bookingID);
    return (
      <ErrorPage
        title={formatMessage(claimsPortalMessages.agentSessionExpiredTitle)}
        leadText={formatMessage(claimsPortalMessages.agentSessionExpiredText)}
        button={
          rcAdminBookingURL
            ? { label: formatMessage(claimsPortalMessages.agentSessionReturnToRCAdmin), link: rcAdminBookingURL }
            : undefined
        }
      />
    );
  }

  if (isGetCustomerError) {
    return <ErrorPage />;
  }

  if (isFetchingRCCustomer || isLoadingRefreshToken || tokenStatus !== 'valid') {
    return <Loading />;
  }

  return <>{children}</>;
};

export default ProtectedLayout;
